/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useNavigate } from "react-router-dom";
import HeaderOptions from "../Components/Common/HeaderOptions";
import ProductLoader from "../Components/Common/ProductLoader";
import ProductBadges from "../Components/ProductBadges";
import { deleteFromWishlist, getPaginatedWishlist } from "../Utils/api-service";
import AppContext from "../Utils/appContext";
import { extractIdFromGid } from "../Utils/categories-divide";
import { addToWishlist, removeFromWishlist } from "../Utils/wishlist-service";
import {
  buildWishlistUiStateMap,
  getWishlistClassName,
  toggleWishlistInMap,
} from "../Utils/wishlist-ui";

const LIMIT = 20;

export default function Wishlist() {
  const navigate = useNavigate();
  const { showPrice } = React.useContext(AppContext);
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [showLoadMore, setShowLoadMore] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [totalCount, setTotalCount] = React.useState(0);
  const [wishlistState, setWishlistState] = React.useState({});

  React.useEffect(() => {
    fetchWishlist(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWishlist = (nextOffset, append) => {
    setLoading(true);
    getPaginatedWishlist(LIMIT, nextOffset)
      .then((data) => {
        if (data.status === 200) {
          const ps = data?.data?.data?.nodes || [];
          const count = data?.data?.count || 0;
          setTotalCount(count);
          setOffset(nextOffset);
          setProducts((prev) => {
            const nextProducts = append ? [...prev, ...ps] : ps;
            setShowLoadMore(count > nextProducts.length);
            setWishlistState(buildWishlistUiStateMap(nextProducts));
            // Merge fetched product ids into local id list (for PLP heart state)
            ps.forEach((product) => addToWishlist(product.id));
            return nextProducts;
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching wishlist:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadMore = () => {
    fetchWishlist(offset + LIMIT, true);
  };

  const [infiniteRef] = useInfiniteScroll({
    loading: loading,
    hasNextPage: showLoadMore,
    onLoadMore: loadMore,
    rootMargin: "0px 0px 400px 0px",
  });

  const goToProduct = (id) => {
    const productId = extractIdFromGid(id);
    navigate("/product?productId=" + productId);
  };

  const toggleWishlist = async (event, product) => {
    event.preventDefault();
    event.stopPropagation();

    setWishlistState((prev) => toggleWishlistInMap(prev, product.id));
    removeFromWishlist(product.id);

    const previousProducts = products;
    const previousCount = totalCount;
    setProducts((prev) => {
      const next = prev.filter((item) => item.id !== product.id);
      const nextCount = Math.max(totalCount - 1, 0);
      setTotalCount(nextCount);
      setShowLoadMore(nextCount > next.length);
      return next;
    });

    const response = await deleteFromWishlist({
      shopifyProductId: product.id,
    });

    if (response?.status !== 200 || response?.data?.removed === false) {
      setWishlistState((prev) => toggleWishlistInMap(prev, product.id));
      addToWishlist(product.id);
      setProducts(previousProducts);
      setTotalCount(previousCount);
      setShowLoadMore(previousCount > previousProducts.length);
    }
  };

  return (
    <>
      <header>
        <div className="back-links">
          <a onClick={() => navigate(-1)}>
            <i className="iconly-Arrow-Left icli"></i>
            <div className="content">
              <h2>
                <span>
                  Your Wishlist <i className="iconly-Heart icbo heart-clr"></i>
                </span>
              </h2>
            </div>
          </a>
        </div>
        <HeaderOptions />
      </header>
      <div className="search-panel top-space px-15"></div>

      {!loading && products.length === 0 ? (
        <section className="px-15">
          <div className="empty-cart-section text-center">
            <img
              src="assets/images/empty-cart.png"
              className="img-fluid"
              alt=""
            />
            <h2>Your Wishlist is Empty</h2>
            <p>
              Looks like you haven’t saved any products yet. Browse categories
              and tap the heart to add items here.
            </p>
            <a
              onClick={() => navigate("/categories")}
              className="btn btn-solid w-100"
            >
              start shopping
            </a>
          </div>
        </section>
      ) : (
        <section className="px-15 lg-t-space">
          <div className="row gy-3 gx-3">
            {products.map((product) => (
              <div key={product.id} className="col-6">
                <div className="product-box ratio_square">
                  <div className="img-part">
                    <a onClick={() => goToProduct(product.id)}>
                      <img
                        src={
                          product?.featuredImage?.src ||
                          "assets/images/products/1000.jpg"
                        }
                        alt={product?.featuredImage?.altText || ""}
                        className="img-fluid bg-img"
                      />
                    </a>
                    <div
                      className={getWishlistClassName(
                        wishlistState[product.id],
                      )}
                      onClick={(event) => toggleWishlist(event, product)}
                      role="button"
                      tabIndex={0}
                      aria-label="Remove from wishlist"
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          toggleWishlist(event, product);
                        }
                      }}
                    >
                      <i className="iconly-Heart icli"></i>
                      <i className="iconly-Heart icbo"></i>
                      {wishlistState[product.id]?.animate && (
                        <div className="effect-group">
                          <span className="effect"></span>
                          <span className="effect"></span>
                          <span className="effect"></span>
                          <span className="effect"></span>
                          <span className="effect"></span>
                        </div>
                      )}
                    </div>
                    {product?.totalInventory > 0 ? (
                      <ProductBadges tags={product?.tags} rotate />
                    ) : (
                      <label>out of stock</label>
                    )}
                  </div>
                  <div
                    onClick={() => goToProduct(product.id)}
                    className="product-content"
                  >
                    <a>
                      <h4>{product?.title}</h4>
                    </a>
                    {showPrice && (
                      <div className="price">
                        {product?.variants?.nodes?.length > 1 ? (
                          <h4>
                            ₹{product?.priceRange?.minVariantPrice?.amount}
                            <span> onwards </span>
                          </h4>
                        ) : (
                          <h4>
                            ₹{product?.priceRange?.minVariantPrice?.amount}
                          </h4>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {showLoadMore && <ProductLoader ref={infiniteRef} />}
        </section>
      )}

      {loading && products.length === 0 && <ProductLoader />}
      <div className="divider"></div>
      <section className="service-wrapper px-15 pt-0">
        <div className="row">
          <div className="col-4">
            <div className="service-wrap">
              <div className="icon-box">
                <img
                  src="assets/svg/about/diamond.svg"
                  className="img-fluid"
                  alt=""
                />
              </div>
              <span>Super Quality</span>
            </div>
          </div>
          <div className="col-4">
            <div className="service-wrap">
              <div className="icon-box">
                <img
                  src="assets/svg/24-hours.svg"
                  className="img-fluid"
                  alt=""
                />
              </div>
              <span>24/7 Support</span>
            </div>
          </div>
          <div className="col-4">
            <div className="service-wrap">
              <div className="icon-box">
                <img src="assets/svg/wallet.svg" className="img-fluid" alt="" />
              </div>
              <span>Best Price</span>
            </div>
          </div>
        </div>
      </section>
      <section className="panel-space"></section>
    </>
  );
}
